import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Mail,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStudyAuth } from "@/context/StudyAuth";
import { resendAccessCode, trackEvent } from "@/lib/api";
import { STUDY } from "@/constants/testIds";
import { CONTACT_EMAIL } from "@/data/content";

const formatAccessCode = (value) => {
  const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
  if (!clean) return "";

  if (clean.startsWith("IBP")) {
    const rest = clean.slice(3);
    return ["IBP", ...(rest.match(/.{1,4}/g) || [])].filter(Boolean).join("-");
  }

  return clean.match(/.{1,4}/g).join("-");
};

const ACCESS_POINTS = [
  "Two Illinois-first lessons",
  "25 practice questions with explanations",
  "40 Word Bank terms",
  "Saved progress on every device",
];

export default function StudyLogin() {
  const { login, requestSignInCode, verifySignInCode } = useStudyAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchased = searchParams.get("purchased") === "1";
  const [mode, setMode] = useState("account");
  const [step, setStep] = useState("request");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState(() => searchParams.get("email") || "");
  const [signInCode, setSignInCode] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const finishSignIn = (result, eventName) => {
    trackEvent(eventName, { access_level: result?.access?.level || "full" });
    const name = result?.license?.first_name;
    const full = result?.access?.has_full_access !== false;
    toast.success(name ? `Welcome, ${name}` : "Welcome to Illinois Broker Prep", {
      description: full
        ? "Your full lifetime access is active."
        : "Your free study account is ready.",
    });
    navigate("/study/dashboard");
  };

  const requestCode = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await requestSignInCode(email, firstName);
      trackEvent("student_signin_code_requested");
      setStep("verify");
      toast.success("Check your email", { description: result.message });
    } catch (error) {
      toast.error("We could not send the sign-in code", {
        description: error?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await verifySignInCode(email, signInCode);
      finishSignIn(result, "student_account_signin_success");
    } catch (error) {
      toast.error("That code did not work", {
        description:
          error?.response?.data?.detail || "Check the six digits or request a new code.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resendSignInCode = async () => {
    setResending(true);
    try {
      const result = await requestSignInCode(email, firstName);
      setSignInCode("");
      toast.success("A new code is on the way", { description: result.message });
    } catch (error) {
      toast.error("We could not resend the code", {
        description: error?.response?.data?.detail || "Please try again shortly.",
      });
    } finally {
      setResending(false);
    }
  };

  const legacyLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, accessCode.trim());
      finishSignIn(result, "legacy_access_code_migrated");
    } catch (error) {
      trackEvent("study_login_failed");
      toast.error("We could not sign you in", {
        description:
          error?.response?.data?.detail ||
          "Check the purchase email and access code, then try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resendLegacyCode = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter your purchase email first");
      return;
    }
    setResending(true);
    try {
      const result = await resendAccessCode(email);
      toast.success("Check your email", { description: result.message });
    } catch (error) {
      toast.error("Could not request the email", {
        description: error?.response?.data?.detail || "Please try again later.",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid={STUDY.loginPage} className="min-h-screen bg-[var(--ibp-offwhite)]">
      <header className="bg-[var(--ibp-navy-950)] text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="text-sm font-bold sm:text-base">
            Illinois Broker Prep
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/75 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(24rem,1.1fr)] lg:items-center lg:py-20">
        <section>
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#9a6a00)]">
            <Sparkles className="h-4 w-4" /> Start learning free
          </p>
          <h1 className="mt-3 max-w-xl text-3xl font-bold leading-tight text-[var(--ibp-ink)] sm:text-4xl">
            One account for your lessons, practice, and progress.
          </h1>
          <p className="mt-4 max-w-xl text-base leading-7 text-[color:rgba(11,18,32,0.7)]">
            Create a free account in under a minute. When you buy full access, use the same email at checkout and the complete platform unlocks automatically.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {ACCESS_POINTS.map((point) => (
              <li key={point} className="flex items-start gap-2 text-sm text-[var(--ibp-ink)]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#15803d]" /> {point}
              </li>
            ))}
          </ul>
          <div className="mt-7 flex items-center gap-3 border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-4 py-3 text-sm text-[color:rgba(11,18,32,0.68)]">
            <ShieldCheck className="h-5 w-5 shrink-0 text-[var(--ibp-navy-950)]" />
            No card for a free account. No password to remember. We email a secure one-time code when you sign in.
          </div>
        </section>

        <section className="border border-[color:rgba(10,27,51,0.13)] bg-white p-6 shadow-[0_16px_44px_rgba(7,20,39,0.09)] sm:p-8">
          {purchased && mode === "account" ? (
            <div className="mb-6 border-l-4 border-l-[#15803d] bg-[color:rgba(21,128,61,0.07)] px-4 py-3">
              <p className="text-sm font-semibold text-[var(--ibp-ink)]">Your purchase is ready to connect.</p>
              <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">
                Enter the same email used at checkout. Full access will appear after sign in.
              </p>
            </div>
          ) : null}

          {mode === "account" ? (
            <>
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
                  {step === "request" ? <UserRound className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--ibp-ink)]">
                    {step === "request" ? "Create an account or sign in" : "Enter your email code"}
                  </h2>
                  <p className="text-sm text-[color:rgba(11,18,32,0.6)]">
                    {step === "request" ? "Free to start. Upgrade only when you are ready." : `We sent six digits to ${email}.`}
                  </p>
                </div>
              </div>

              {step === "request" ? (
                <form onSubmit={requestCode} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="student-first-name">First name <span className="font-normal text-[color:rgba(11,18,32,0.45)]">(optional)</span></Label>
                    <Input
                      id="student-first-name"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder="First name"
                      className="mt-1.5 h-11 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="study-email">Email</Label>
                    <Input
                      id="study-email"
                      type="email"
                      required
                      autoComplete="email"
                      data-testid={STUDY.loginEmail}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@email.com"
                      className="mt-1.5 h-11 rounded-lg"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid={STUDY.loginSubmit}
                    disabled={submitting || !email}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
                  >
                    <Mail className="h-4 w-4" /> {submitting ? "Sending..." : "Email me a secure code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyCode} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="student-signin-code">Six-digit code</Label>
                    <Input
                      id="student-signin-code"
                      required
                      autoFocus
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      data-testid={STUDY.loginCode}
                      value={signInCode}
                      onChange={(event) => setSignInCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="mt-1.5 h-14 rounded-lg text-center text-2xl font-bold tracking-[0.35em]"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid={STUDY.loginSubmit}
                    disabled={submitting || signInCode.length !== 6}
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
                  >
                    {submitting ? "Signing in..." : "Continue to my account"}
                  </button>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                    <button type="button" onClick={() => { setStep("request"); setSignInCode(""); }} className="font-semibold text-[#0F3D8A] hover:underline">
                      Change email
                    </button>
                    <button type="button" onClick={resendSignInCode} disabled={resending} className="font-semibold text-[#0F3D8A] hover:underline disabled:opacity-60">
                      {resending ? "Sending..." : "Send a new code"}
                    </button>
                  </div>
                </form>
              )}

              <p className="mt-5 text-center text-xs leading-5 text-[color:rgba(11,18,32,0.5)]">
                By continuing, you agree to the <Link to="/terms" className="font-semibold text-[#0F3D8A] hover:underline">Terms of Service</Link> and acknowledge the <Link to="/privacy" className="font-semibold text-[#0F3D8A] hover:underline">Privacy Policy</Link>.
              </p>

              <div className="mt-6 border-t border-[color:rgba(10,27,51,0.1)] pt-5 text-center">
                <p className="text-sm text-[color:rgba(11,18,32,0.62)]">Bought before account sign-in was introduced?</p>
                <button type="button" onClick={() => { setMode("legacy"); setStep("request"); }} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline">
                  <KeyRound className="h-4 w-4" /> Use an existing access code
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
                  <KeyRound className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--ibp-ink)]">Existing buyer sign in</h2>
                  <p className="text-sm text-[color:rgba(11,18,32,0.6)]">Your progress will move into your account automatically.</p>
                </div>
              </div>
              <form onSubmit={legacyLogin} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="legacy-email">Purchase email</Label>
                  <Input id="legacy-email" type="email" required autoComplete="email" data-testid={STUDY.loginEmail} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@email.com" className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div>
                  <Label htmlFor="legacy-code">Access code</Label>
                  <Input id="legacy-code" required autoCapitalize="characters" autoComplete="one-time-code" data-testid={STUDY.loginCode} value={accessCode} onChange={(event) => setAccessCode(formatAccessCode(event.target.value))} placeholder="IBP-XXXX-XXXX" className="mt-1.5 h-11 rounded-lg" />
                </div>
                <button type="submit" data-testid={STUDY.loginSubmit} disabled={submitting || !email || !accessCode} className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60">
                  {submitting ? "Moving your account..." : "Sign in and keep my progress"}
                </button>
              </form>
              <div className="mt-5 flex flex-col items-center gap-2 text-sm">
                <button type="button" data-testid={STUDY.loginResend} onClick={resendLegacyCode} disabled={resending} className="inline-flex items-center gap-1.5 font-semibold text-[#0F3D8A] hover:underline disabled:opacity-60">
                  <Mail className="h-4 w-4" /> {resending ? "Sending..." : "Email my old code"}
                </button>
                <button type="button" onClick={() => setMode("account")} className="font-semibold text-[#0F3D8A] hover:underline">
                  Back to email sign in
                </button>
              </div>
            </>
          )}

          <p className="mt-6 text-center text-xs leading-5 text-[color:rgba(11,18,32,0.5)]">
            Need help? Email <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-[#0F3D8A] hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </main>
    </div>
  );
}
